import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Outlet,
  useLocation,
  useSearchParams,
} from "react-router-dom"

import Breadcrumbs from "@components/filter/Breadcrumbs"
import SnippetTypesFilterButtons from "@components/filter/SnippetTypesFilterButtons"
import FloatingMenu from "@components/FloatingMenu"
import Header from "@components/header/Header"
import SnippetsTabs from "@components/snippets-ui/tabs/SnippetsTabs"

import {
  bookmarkColor,
  flag,
  folder,
  isSearching,
  search,
  setOthersSnippets,
  setPinnedSnippets,
  snippets,
  starred,
  type,
} from "rtk-slice"

import { snippetsFilter } from "utils/filter"
import { isMobile } from "utils/mediaQuery"
import toggleCapitalization from "utils/toggleCapitalization"

import {
  CircularProgress,
  Container,
  Stack,
  styled,
  Toolbar,
} from "@mui/material"

import loadable from "@loadable/component"
import useTitle from "hooks/useTitle"
import { snippetTypes } from "icons-symbols/snippetTypes"

import { getUsageFromFirebase } from "../features/firebase/firebaseFunctions"
import { router } from "../routes/router"
import Cookies from "./Cookies"

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  marginBottom: "3em",
  px: "0em",
  ...theme.typography.body2,
}))

const LoadableCmdkDialog = loadable(() =>
  import("@components/cmdk/CmdkDialog")
)

const RootLayout = () => {
  const dispatch = useDispatch()
  const [isUsageLoaded, setIsUsageLoaded] = useState(false)

  const snippetsState = useSelector(snippets)
  const flaggedState = useSelector(flag)
  const starredState = useSelector(starred)
  const bookmarkColorState = useSelector(bookmarkColor)
  const folderState = useSelector(folder)
  const typeState = useSelector(type)
  const searchState = useSelector(search)
  const isSearchingState = useSelector(isSearching)
  const { pathname } = useLocation()
  const pageTitle = useMemo(
    () =>
      pathname.includes("edit")
        ? "Snippet Editor"
        : pathname === "/"
        ? "Snippify | Desktop"
        : toggleCapitalization("upper", pathname.slice(1)),
    [pathname]
  )
  useTitle(pageTitle)

  const [, setSearchParams] = useSearchParams()

  const filteredPinnedSnippets = useMemo(() => {
    const swapSnippets = snippetsState.filter(
      (snippet) => snippet.isPinned
    )
    return snippetsFilter(
      searchState,
      flaggedState,
      starredState,
      bookmarkColorState,
      folderState,
      typeState,
      snippetTypes,
      swapSnippets
    )
  }, [
    bookmarkColorState,
    flaggedState,
    folderState,
    snippetsState,
    searchState,
    starredState,
    typeState,
  ])

  const filteredOthersSnippets = useMemo(() => {
    const swapSnippets = snippetsState.filter(
      (snippet) => !snippet.isPinned
    )
    return snippetsFilter(
      searchState,
      flaggedState,
      starredState,
      bookmarkColorState,
      folderState,
      typeState,
      snippetTypes,
      swapSnippets
    )
  }, [
    bookmarkColorState,
    flaggedState,
    folderState,
    snippetsState,
    searchState,
    starredState,
    typeState,
  ])

  const handleGetUsage = useCallback(async () => {
    try {
      await getUsageFromFirebase({ dispatch })
      setIsUsageLoaded(true)
    } catch (error) {
      setIsUsageLoaded(true)
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(setPinnedSnippets(filteredPinnedSnippets))
    dispatch(setOthersSnippets(filteredOthersSnippets))
  }, [dispatch, filteredOthersSnippets, filteredPinnedSnippets])

  useEffect(() => {
    if (folderState) {
      router.navigate("/snippets")
    }
    if (searchState !== "") {
      setSearchParams({ keyword: searchState })
    }
    if (searchState === "") {
      setSearchParams()
    }
  }, [folderState, searchState, setSearchParams])

  useEffect(() => {
    handleGetUsage()
  }, [handleGetUsage])

  return (
    <Root>
      <Header />
      <Toolbar sx={{ height: "100px" }} />
      <main>
        <Stack
          justifyContent={"center"}
          alignItems="center"
          direction={isMobile() ? "column" : "row"}
          px={isMobile() ? "0em" : "8em"}
          pt="1em"
        >
          <Breadcrumbs />
        </Stack>
        <Stack
          justifyContent={"space-between"}
          alignItems="center"
          direction={isMobile() ? "column" : "row"}
          px={isMobile() ? "0em" : "8em"}
          pt="1em"
          borderColor={"GrayText"}
        >
          <SnippetTypesFilterButtons />
          {pathname === "/snippets" && <SnippetsTabs />}
        </Stack>
        {isUsageLoaded ? (
          <Outlet />
        ) : (
          <Container
            maxWidth="xl"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "calc(100dvh - 18em)",
            }}
          >
            <CircularProgress size={50} />
          </Container>
        )}
        <FloatingMenu folderState={folderState} />
      </main>
      <Cookies />
      {isSearchingState ? <LoadableCmdkDialog /> : null}
    </Root>
  )
}

export default RootLayout
